import React from 'react';

import { HUB_AREA, type HubArea } from '../../../../theme/tokens';

/** A glyph sweep's three stops, light to deep. */
export type HubInk = { inkFrom: string; inkMid: string; inkTo: string };
import { InkDefs, InkStop } from '../../website-hub.styles';

export type HubGlyphProps = (
  | {
      /** The card's area: the sweep is in its glow's hue. */
      area: HubArea;
      ink?: undefined;
    }
  | {
      area?: undefined;
      /** A sweep of its own, for a glyph outside the hubs (the Website analytics tiles). */
      ink: HubInk;
    }
) & {
  /** The glyph's viewBox width: the ink sweeps across exactly that span (24 for the shared `Icon`). */
  span: number;
  /** Draws the glyph in the paint it is given (an SVG `url(#…)` reference to the ink). */
  children: (ink: string) => React.ReactNode;
};

/**
 * A hub tile's glyph, painted with its area's sweep in the card's glow hue, deep at the left to light at the right,
 * across the whole glyph in its own units, so every stroke shares one sweep rather than restarting per shape. The
 * gradient's definition is placed after the glyph, so the glyph stays the tile's first svg. Shared by the Website and
 * Marketing hubs (and the Website analytics tiles, with their own `ink`).
 */
export const HubGlyph = ({ area, ink: own, span, children }: HubGlyphProps): React.ReactElement => {
  const ink = own ?? HUB_AREA[area];
  const id = `hub-icon-ink-${React.useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  return (
    <>
      {children(`url(#${id})`)}
      <InkDefs aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={id} gradientUnits="userSpaceOnUse" x1={span} y1="0" x2="0" y2="0">
            <InkStop offset="0%" $color={ink.inkFrom} />
            <InkStop offset="50%" $color={ink.inkMid} />
            <InkStop offset="100%" $color={ink.inkTo} />
          </linearGradient>
        </defs>
      </InkDefs>
    </>
  );
};

export default HubGlyph;
